import { useEffect, useRef, useState } from "react";
import { IconX, IconVolume } from "@tabler/icons-react";

type Props = {
  open: boolean;
  onClose: () => void;
};

export function DemoVideoModal({ open, onClose }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Start muted so autoplay can succeed reliably.
  // We prefer sound and only stay muted if the browser blocks unmuted autoplay.
  const [muted, setMuted] = useState(true);

  // Handle Escape key + focus management while open.
  // Also kicks off autoplay (prefers sound, falls back to muted).
  useEffect(() => {
    if (!open) return;

    // Capture for focus return on close
    previouslyFocused.current = document.activeElement as HTMLElement | null;

    // Focus the close button shortly after mount for good keyboard UX.
    const t = window.setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 0);

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        handleClose();
      }
    }

    document.addEventListener("keydown", onKey);

    // Autoplay logic: try unmuted first (some contexts allow it).
    // If blocked (NotAllowedError), force muted autoplay + show unmute UI.
    let playTimer: number | undefined;
    const video = videoRef.current;
    if (video) {
      const tryAutoplay = async () => {
        try {
          // Optimistic: try with sound
          video.muted = false;
          setMuted(false);
          await video.play();
        } catch (err: any) {
          if (err?.name === "NotAllowedError") {
            // Browser policy blocked unmuted autoplay — fall back safely
            video.muted = true;
            setMuted(true);
            try {
              await video.play();
            } catch {
              // Even muted autoplay was blocked; user can use native controls
            }
          }
        }
      };

      // Small delay ensures the video element is fully ready after mount
      playTimer = window.setTimeout(() => {
        void tryAutoplay();
      }, 50);
    }

    return () => {
      document.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
      if (playTimer) window.clearTimeout(playTimer);
    };
  }, [open]);

  function handleClose() {
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
    // Return focus to whatever had it before (usually the hero button).
    onClose();
    // Restore focus after the modal unmounts.
    window.setTimeout(() => {
      previouslyFocused.current?.focus?.();
    }, 0);
  }

  function handleUnmute() {
    const video = videoRef.current;
    if (video) {
      video.muted = false;
    }
    setMuted(false);
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
      onClick={handleClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="JobWarp demo video"
        className="relative w-full max-w-[1100px] overflow-hidden rounded-xl border border-default bg-panel shadow-panel"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Slim header bar for title + close affordance (matches system panels) */}
        <div className="flex items-center justify-between border-b border-default px-4 py-2.5">
          <span className="text-13 font-medium text-primary">
            JobWarp demo
          </span>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={handleClose}
            className="icon-btn"
            aria-label="Close demo video"
          >
            <IconX size={16} />
          </button>
        </div>

        {/* Video area — native controls + autoplay on open.
            Starts muted (with visible Unmute affordance) unless the browser
            permits unmuted autoplay on this context. */}
        <div className="relative">
          <video
            ref={videoRef}
            className="block w-full h-auto bg-black"
            controls
            autoPlay
            muted={muted}
            playsInline
            preload="metadata"
            onVolumeChange={() => {
              const v = videoRef.current;
              if (v && !v.muted) setMuted(false);
            }}
          >
            <source src="/videos/jobwarp-promo.webm" type="video/webm" />
            <source src="/videos/jobwarp-promo.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          {/* Floating unmute hint — only while muted. Matches design system. */}
          {muted && (
            <button
              type="button"
              onClick={handleUnmute}
              className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-panel/90 border border-strong px-2.5 py-1 text-11 text-primary transition-colors hover:bg-elevated"
            >
              <IconVolume size={14} />
              Unmute
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
